/**
 * Krisar Vastrae - On-Card Cart Controller
 * Allows users to add, increment, decrement, and remove items directly from product cards.
 * Automatically synchronizes with Shopify Cart Drawer and Header Bubble.
 */
(function() {
  'use strict';

  function getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((s) => s.id).join(',');
    }
    return 'cart-drawer,cart-icon-bubble';
  }

  function updateCartDrawerUI(data, openDrawer = true) {
    if (!data) return;

    // Render section HTML in cart-drawer & cart-icon-bubble
    if (data.sections) {
      const activeCartDrawer = document.querySelector('cart-drawer');
      if (activeCartDrawer) {
        activeCartDrawer.classList.remove('is-empty');
        activeCartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
        activeCartDrawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        activeCartDrawer.querySelector('#CartDrawer')?.classList.remove('is-empty');

        if (typeof activeCartDrawer.renderContents === 'function') {
          activeCartDrawer.renderContents(data);
        } else {
          for (const [sectionId, html] of Object.entries(data.sections)) {
            const target = document.getElementById(sectionId);
            if (target) {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const inner = parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
              if (inner) target.innerHTML = inner.innerHTML;
            }
          }
          if (openDrawer && typeof activeCartDrawer.open === 'function') {
            activeCartDrawer.open();
          }
        }
      }

      // Update cart bubble in header
      if (data.sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const newBubble = parsed.querySelector('#cart-icon-bubble') || parsed.body.firstElementChild;
          if (newBubble) bubble.innerHTML = newBubble.innerHTML;
        }
      }
    }
  }

  function syncAllCardsWithCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const variantCounts = {};
    cart.items.forEach((item) => {
      const vId = String(item.variant_id || item.id);
      variantCounts[vId] = (variantCounts[vId] || 0) + item.quantity;
    });

    const containers = document.querySelectorAll('.krisar-card-action-container');
    containers.forEach((container) => {
      const variantId = String(container.dataset.variantId || '');
      if (!variantId) return;

      const qty = variantCounts[variantId] || 0;
      const addBtn = container.querySelector('.js-krisar-quick-add');
      const qtyWrapper = container.querySelector('.js-krisar-card-qty');
      const countDisplay = container.querySelector('.js-krisar-qty-val');
      const card = container.closest('.krisar-product-card') || container.closest('.card-wrapper') || container.closest('.card');

      if (qty > 0) {
        if (addBtn) addBtn.style.display = 'none';
        if (qtyWrapper) qtyWrapper.style.display = 'flex';
        if (countDisplay) countDisplay.textContent = qty;
        container.classList.add('has-items-in-cart');
        if (card) card.classList.add('has-items-in-cart');
      } else {
        if (addBtn) {
          addBtn.style.display = 'flex';
          addBtn.classList.remove('is-loading', 'is-success');
        }
        if (qtyWrapper) {
          qtyWrapper.style.display = 'none';
          qtyWrapper.classList.remove('is-loading');
        }
        if (countDisplay) countDisplay.textContent = '1';
        container.classList.remove('has-items-in-cart');
        if (card) card.classList.remove('has-items-in-cart');
      }
    });
  }

  function fetchCartAndSync() {
    const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
    fetch(cartUrl)
      .then((res) => res.json())
      .then((cart) => {
        syncAllCardsWithCart(cart);
      })
      .catch((err) => {
        console.warn('Could not fetch cart for card sync:', err);
      });
  }

  // Handle Quick Add / Add to Cart Click
  function handleCardAdd(btn) {
    const container = btn.closest('.krisar-card-action-container');
    const variantId = btn.dataset.variantId || container?.dataset.variantId;
    if (!variantId) {
      // If no direct variant ID, follow link
      const link = btn.closest('.krisar-product-card, .card-wrapper, .card')?.querySelector('a');
      if (link) window.location.href = link.href;
      return;
    }

    btn.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', getSectionsToRender());
    formData.append('sections_url', window.location.pathname);

    const addUrl = window.routes?.cart_add_url || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    })
      .then((res) => res.json())
      .then((data) => {
        btn.classList.remove('is-loading');

        if (data.status || data.errors) {
          const errorMsg = data.description || data.message || 'Sold Out';
          const btnText = btn.querySelector('.btn-text');
          if (btnText) {
            const prev = btnText.textContent;
            btnText.textContent = errorMsg;
            setTimeout(() => { btnText.textContent = prev; }, 2500);
          }
          return;
        }

        // Switch to quantity controller on card
        if (container) {
          const qtyWrapper = container.querySelector('.js-krisar-card-qty');
          const countDisplay = container.querySelector('.js-krisar-qty-val');
          btn.style.display = 'none';
          if (qtyWrapper) qtyWrapper.style.display = 'flex';
          if (countDisplay) countDisplay.textContent = '1';
          container.classList.add('has-items-in-cart');
          const card = container.closest('.krisar-product-card, .card-wrapper, .card');
          if (card) card.classList.add('has-items-in-cart');
        }

        // Update Cart Drawer and open
        updateCartDrawerUI(data, true);

        // Notify subscribers
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'krisar-card-cart',
            productVariantId: variantId,
            cartData: data
          });
        }
      })
      .catch((err) => {
        console.error('Error adding item from card:', err);
        btn.classList.remove('is-loading');
      });
  }

  // Handle Quantity Change (+ or -)
  function handleCardQtyChange(btn, isIncrement) {
    const container = btn.closest('.krisar-card-action-container');
    const qtyWrapper = container?.querySelector('.js-krisar-card-qty');
    const countDisplay = container?.querySelector('.js-krisar-qty-val');
    const variantId = container?.dataset.variantId;
    if (!container || !variantId || !countDisplay) return;

    const currentQty = parseInt(countDisplay.textContent || '1', 10);
    const targetQty = isIncrement ? currentQty + 1 : currentQty - 1;
    if (targetQty < 0) return;

    if (qtyWrapper) qtyWrapper.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(targetQty));
    formData.append('sections', getSectionsToRender());
    formData.append('sections_url', window.location.pathname);

    const changeUrl = window.routes?.cart_change_url || '/cart/change.js';

    fetch(changeUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    })
      .then((res) => res.json())
      .then((data) => {
        if (qtyWrapper) qtyWrapper.classList.remove('is-loading');

        if (data.status || data.errors) {
          console.warn('Quantity update error:', data.message);
          return;
        }

        if (targetQty === 0) {
          // Revert to quick add button
          const addBtn = container.querySelector('.js-krisar-quick-add');
          if (addBtn) addBtn.style.display = 'flex';
          if (qtyWrapper) qtyWrapper.style.display = 'none';
          countDisplay.textContent = '1';
          container.classList.remove('has-items-in-cart');
          const card = container.closest('.krisar-product-card, .card-wrapper, .card');
          if (card) card.classList.remove('has-items-in-cart');
        } else {
          countDisplay.textContent = String(targetQty);
        }

        // Sync all other cards on page with identical variant ID
        const matchingContainers = document.querySelectorAll(`.krisar-card-action-container[data-variant-id="${variantId}"]`);
        matchingContainers.forEach((other) => {
          if (other === container) return;
          const otherAddBtn = other.querySelector('.js-krisar-quick-add');
          const otherQtyWrapper = other.querySelector('.js-krisar-card-qty');
          const otherCount = other.querySelector('.js-krisar-qty-val');
          if (targetQty === 0) {
            if (otherAddBtn) otherAddBtn.style.display = 'flex';
            if (otherQtyWrapper) otherQtyWrapper.style.display = 'none';
            if (otherCount) otherCount.textContent = '1';
            other.classList.remove('has-items-in-cart');
          } else {
            if (otherAddBtn) otherAddBtn.style.display = 'none';
            if (otherQtyWrapper) otherQtyWrapper.style.display = 'flex';
            if (otherCount) otherCount.textContent = String(targetQty);
            other.classList.add('has-items-in-cart');
          }
        });

        // Update Cart Drawer UI
        updateCartDrawerUI(data, isIncrement);

        // Notify subscribers
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'krisar-card-cart',
            productVariantId: variantId,
            cartData: data
          });
        }
      })
      .catch((err) => {
        console.error('Error changing item quantity from card:', err);
        if (qtyWrapper) qtyWrapper.classList.remove('is-loading');
      });
  }

  // Delegated Global Event Listener
  document.addEventListener('click', function(e) {
    const quickAddBtn = e.target.closest('.js-krisar-quick-add');
    if (quickAddBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleCardAdd(quickAddBtn);
      return;
    }

    const plusBtn = e.target.closest('.js-krisar-qty-plus');
    if (plusBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleCardQtyChange(plusBtn, true);
      return;
    }

    const minusBtn = e.target.closest('.js-krisar-qty-minus');
    if (minusBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleCardQtyChange(minusBtn, false);
      return;
    }
  });

  // Listen for Cart Updates across the site
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
      if (event && event.cartData && event.cartData.items) {
        syncAllCardsWithCart(event.cartData);
      } else {
        fetchCartAndSync();
      }
    });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCartAndSync);
  } else {
    fetchCartAndSync();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function() {
    fetchCartAndSync();
  });
})();
